import type { ThreeEvent } from '@react-three/fiber';
import { useCallback, useEffect, useEffectEvent, useRef } from 'react';
import {
  getHexTileMapKey,
  type HexTileCoordinates,
  type HexTileMapKey,
  hexMapConfig,
  worldToAxial,
} from '@/game/hexmap';
import { type Command, canAttack, canFoundCity, canHit, canWatch, isHidden } from '@/game/sim';
import { cityName } from '@/game/view/cities';
import type { Frame, FrameStore } from '@/game/view/frame';
import { fogCityAt } from '@/game/view/lastSeen';
import { cityTiles, placementCheck } from '@/game/view/placement';
import { foundCityRejectionText, placeRejectionText } from '@/game/view/reasons';
import { stockInUnits } from '@/game/view/resources';
import { rivalSprawlCity } from '@/game/view/sprawl';
import { attackMoveCommand } from '@/game/view/stance';
import { workTargetAt } from '@/game/view/workTarget';
import { DRAG_PX } from '../ui/boxSelect';
import type { TargetMode, UiStore } from '../ui/store';
import type { PushToasts } from './useCommandFeedback';
import type { MatchModes } from './useMatchModes';

type PointerDownInfo = {
  x: number;
  y: number;
  button: number;
  tileCoords: HexTileCoordinates;
  /** Unit whose model the press landed on (nearest first), if any. */
  unitId: string | null;
} | null;

/**
 * Why a targeting click on `cell` is refused (null: it goes through).
 * Fog-safe: an unexplored tile is refused before anything else is looked at
 * (a watch spot may be in fog: watching is how the fog is looked into).
 */
function targetRefusal(
  frame: Frame,
  mode: TargetMode,
  cell: HexTileCoordinates,
  foundable: ReadonlySet<HexTileMapKey> | null
): string | null {
  const { view: state, ctx, playerId, rules, fog } = frame;
  // Any tile will do, like a move order (the sim walks to the nearest reachable one).
  if (mode.kind === 'attackMove') return null;
  if (mode.kind === 'watch') {
    const check = canWatch(state, ctx, playerId, mode.buildingId, cell);
    if (check.ok) return null;
    return check.reason === 'range'
      ? `Too far: the tower watches up to ${check.range} tiles away`
      : check.reason === 'not-ready'
        ? 'The tower is still being built'
        : 'Pick a tile on the map';
  }
  const key = getHexTileMapKey(cell);
  if (!fog.explored.has(key)) return "You haven't explored that tile";
  // A unit the player sees standing there would keep the settler off the
  // tile (own units, or rivals' in sight; a hidden spy holds no tile).
  const standing = Object.values(state.units).some(
    (u) =>
      u.id !== mode.unitId &&
      !u.inside &&
      !isHidden(rules, u) &&
      getHexTileMapKey(u.pos) === key &&
      (u.owner === playerId || fog.visible.has(key))
  );
  if (standing) return 'A unit is standing there: move it first';
  // The tinted tiles, judged from the buildings the player knows of.
  if (foundable?.has(key)) return null;
  const check = canFoundCity(state, ctx, playerId, mode.unitId, frame.knownBuilding, cell);
  if (check.ok) return "The settler can't stop there";
  return foundCityRejectionText(rules, check, (id) => {
    const city = state.buildings[id];
    return city ? cityName(city, playerId, frame.cityNumbers, frame.playerName, frame.names) : null;
  });
}

/**
 * What a right-click is aimed at: the rival unit model under the cursor,
 * else a visible rival unit or a known rival building on the tile, else the
 * rival city whose sprawl covers it in sight (the tile looks like part of
 * the city, so it's aimed at it; `cell` is then the city's tile), else a
 * remembered rival city next to the tile out of sight (`fogCityAt`).
 */
function enemyTargetAt(
  frame: Frame,
  info: NonNullable<PointerDownInfo>
): { id: string; cell?: HexTileCoordinates } | null {
  const { view: state, playerId, fog } = frame;
  const pressed = state.units[info.unitId ?? ''];
  if (pressed && pressed.owner !== playerId) return { id: pressed.id };
  const key = getHexTileMapKey(info.tileCoords);
  if (fog.visible.has(key)) {
    const unit = Object.values(state.units).find(
      (u) => u.owner !== playerId && getHexTileMapKey(u.pos) === key
    );
    if (unit) return { id: unit.id };
  }
  const building = frame.knownBuilding(key);
  // A remembered building may be gone; the sim then moves to the tile.
  if (building && building.owner !== playerId) return { id: building.id };
  const city = rivalSprawlCity(state, frame.ctx, playerId, fog.sight, key);
  if (city) return { id: city.id, cell: { q: city.pos.q, r: city.pos.r } };
  // Out of sight: the ring round a city the player remembers there (as last
  // seen, fog-safe), so a click on a fogged city's edge attacks it.
  const fogged = fogCityAt(frame.knownBuildings(), frame.rules, playerId, key, fog.sight);
  return fogged ? { id: fogged.id, cell: { q: fogged.pos.q, r: fogged.pos.r } } : null;
}

/**
 * Map input: presses on tiles, unit models and fog city markers, decided on
 * pointer up (a drag is the camera's with right or middle, box-select's with
 * left). Left-click selects or inspects, right-click orders the selection to
 * move or attack (shift queues the attack as the next focus target; a move shows its marker at once, before the host confirms
 * it) and assigns its workers to work under the cursor (`workTargetAt`); in placement
 * or targeting mode the click is consumed by the mode, with the reason when
 * the tile won't do. Also tracks the hovered tile in the input store.
 */
export function usePointerOrders({
  ui,
  frames,
  modes,
  dispatch,
  pushToasts,
  setInspectKey,
  onGroundDown,
}: {
  ui: UiStore;
  frames: FrameStore;
  modes: MatchModes;
  dispatch: (command: Command) => void;
  pushToasts: PushToasts;
  setInspectKey: (key: HexTileMapKey | null) => void;
  /** A press on the ground (not on a unit, building or marker), for box-select. */
  onGroundDown?: (event: PointerEvent) => void;
}) {
  const pointerDownRef = useRef<PointerDownInfo>(null);
  // A unit model under the press that didn't take the click (right-click, or
  // any click while placing). Unit handlers run before the tile's (nearest
  // first), so the tile handler picks it up.
  const pressedUnitRef = useRef<string | null>(null);
  const onUnitPointerDown = useCallback((id: string) => {
    pressedUnitRef.current ??= id;
  }, []);
  // Stable, so the memo'd HexMap never re-renders for it.
  const onTilePointerDown = useCallback(
    (coords: HexTileCoordinates, event: ThreeEvent<PointerEvent>) => {
      pointerDownRef.current = {
        x: event.nativeEvent.clientX,
        y: event.nativeEvent.clientY,
        button: event.button,
        tileCoords: coords,
        unitId: pressedUnitRef.current,
      };
      pressedUnitRef.current = null;
      onGroundDown?.(event.nativeEvent);
    },
    [onGroundDown]
  );
  // A press on a fog city marker counts as a press on its city's tile (the
  // marker floats above it, so the tile under the cursor is another one).
  const onMarkerPointerDown = useCallback((pos: HexTileCoordinates, event: PointerEvent) => {
    pressedUnitRef.current = null;
    pointerDownRef.current = {
      x: event.clientX,
      y: event.clientY,
      button: event.button,
      tileCoords: { q: pos.q, r: pos.r },
      unitId: null,
    };
  }, []);

  // Targeting (watch spot, founding spot): like placement, the click is
  // consumed; a click on a unit model picks the tile it stands on.
  const targetClick = (frame: Frame, info: NonNullable<PointerDownInfo>, mode: TargetMode) => {
    const pressed = frame.view.units[info.unitId ?? ''];
    const cell = pressed ? { q: pressed.pos.q, r: pressed.pos.r } : info.tileCoords;
    if (info.button === 2) {
      ui.set({ targetMode: null });
      return;
    }
    if (info.button !== 0) return;
    const refusal = targetRefusal(frame, mode, cell, modes.foundable);
    if (refusal === null) {
      if (mode.kind === 'attackMove') {
        const picked = ui.get().selectedUnitIds;
        const command = attackMoveCommand(frame.view, frame.rules, frame.playerId, picked, cell);
        if (command && command.type === 'attackMove') {
          dispatch(command);
          ui.set({ pendingMove: { cell, unitIds: command.unitIds, at: Date.now() } });
        }
      } else {
        dispatch(
          mode.kind === 'watch'
            ? { type: 'watch', buildingId: mode.buildingId, target: cell }
            : { type: 'foundAt', unitId: mode.unitId, target: cell }
        );
      }
      ui.set({ targetMode: null });
    } else {
      modes.setTargetError(refusal);
      pushToasts([{ text: refusal, tone: 'warn' }]);
    }
  };

  // Placement: the click is consumed. A click on a unit model targets the
  // tile the unit stands on (units don't take clicks here).
  const placeClick = (frame: Frame, info: NonNullable<PointerDownInfo>, shift: boolean) => {
    const { placementMode: placing, placementCityId } = ui.get();
    if (!placing) return;
    const pressed = frame.view.units[info.unitId ?? ''];
    const cell = pressed ? { q: pressed.pos.q, r: pressed.pos.r } : info.tileCoords;
    if (info.button === 2) {
      // Right-click cancels placement without issuing a move.
      ui.set({ placementMode: null });
      return;
    }
    if (info.button !== 0) return;
    // Checked here too so a bad tile keeps the mode open with the reason for
    // that tile (the sim has the final say). Fog-safe: unexplored tiles are
    // refused without looking at their terrain, and only known buildings count.
    const { view: state, ctx, playerId, rules, terrain } = frame;
    const key = getHexTileMapKey(cell);
    const city = state.buildings[placementCityId ?? ''];
    const check = placementCheck(
      state,
      ctx,
      playerId,
      placing,
      cell,
      city ? cityTiles(state, rules, city.id) : new Set(),
      frame.fog.explored.has(key),
      (k) => frame.knownBuilding(k) !== null
    );
    if (check.ok) {
      dispatch({ type: 'place', buildingType: placing, target: cell });
      modes.setPlacementError(null);
      // Shift keeps placing.
      if (!shift) ui.set({ placementMode: null });
      return;
    }
    const text = placeRejectionText(rules, placing, check.reason, stockInUnits(state, playerId), {
      tileType: terrain.get(key)?.type,
      city: city
        ? cityName(city, playerId, frame.cityNumbers, frame.playerName, frame.names)
        : undefined,
    });
    modes.setPlacementError({ key, text });
    // The banner is easy to miss: a toast too (repeats merge, "×3").
    pushToasts([{ text, tone: 'warn' }]);
  };

  // A plain click: right-click orders the selection, left-click on a rival
  // inspects its tile (keeping the selection, so it can then be sent there),
  // left-click on the ground clears the selection (shift-click keeps it).
  const orderClick = (frame: Frame, info: NonNullable<PointerDownInfo>, shift: boolean) => {
    if (info.button === 2) {
      const picked = ui.get().selectedUnitIds;
      if (picked.length === 0) return;
      const { view: state, rules } = frame;
      const target = enemyTargetAt(frame, info);
      // Only a rival's model redirects the order to its tile; over an own
      // unit's model the ground under the cursor is what was clicked.
      const pressed = state.units[info.unitId ?? ''];
      const onRival = !!pressed && pressed.owner !== frame.playerId;
      const cell = onRival ? { q: pressed.pos.q, r: pressed.pos.r } : info.tileCoords;
      // Workers over something to work are pinned to it; the rest of the
      // selection keeps the order below.
      const work = onRival ? null : workTargetAt(frame, picked, cell);
      if (work) {
        dispatch({ type: 'assign', unitIds: work.workers, target: work.target, pin: true });
        ui.set({ pendingMove: { cell, unitIds: work.workers, at: Date.now() } });
      }
      const selected = work ? work.others : picked;
      if (selected.length === 0) return;
      // A sprawl tile aims at its city only for a selection that can hit
      // it; anyone else moves to the tile clicked, not into city fire.
      const hitsCity = selected.some((id) => {
        const u = state.units[id];
        return !!u && canAttack(rules, u) && canHit(rules, u.type, false);
      });
      if (target && (!target.cell || hitsCity)) {
        // `target` doubles as a move if the target turns out to be gone.
        // Shift queues it behind the current focus (a unit with no manual
        // attack order simply attacks now).
        dispatch({
          type: 'attack',
          unitIds: [...selected],
          targetId: target.id,
          target: target.cell ?? cell,
          ...(shift && { queue: true as const }),
        });
      } else {
        dispatch({ type: 'move', unitIds: [...selected], target: cell });
        ui.set({ pendingMove: { cell, unitIds: [...selected], at: Date.now() } });
      }
    } else if (info.button === 0 && !shift) {
      const target = enemyTargetAt(frame, info);
      if (target) {
        setInspectKey(getHexTileMapKey(target.cell ?? info.tileCoords));
        return;
      }
      ui.set({ selectedUnitIds: [], selectedBuildingId: null });
      setInspectKey(null);
    }
  };

  // Decided on pointer up, to tell a click from a drag.
  const onPointerUp = useEffectEvent((e: PointerEvent) => {
    const info = pointerDownRef.current;
    pressedUnitRef.current = null;
    if (!info) return;
    pointerDownRef.current = null;
    if (Math.hypot(e.clientX - info.x, e.clientY - info.y) > DRAG_PX) return;
    const frame = frames.get();
    const { targetMode, placementMode } = ui.get();
    if (targetMode) targetClick(frame, info, targetMode);
    else if (placementMode) placeClick(frame, info, e.shiftKey);
    else orderClick(frame, info, e.shiftKey);
  });
  useEffect(() => {
    const listener = (e: PointerEvent) => onPointerUp(e);
    window.addEventListener('pointerup', listener);
    return () => window.removeEventListener('pointerup', listener);
  }, []);

  // The hovered tile, in the store: it changes only when the cursor crosses
  // into a different hex, and only its subscribers re-render.
  const onBoardPointerMove = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      const cell = worldToAxial(e.point.x, e.point.z, hexMapConfig.hexMap.tile.radius);
      ui.set({ hoveredKey: getHexTileMapKey(cell) });
    },
    [ui]
  );
  const onBoardPointerOut = useCallback(() => ui.set({ hoveredKey: null }), [ui]);

  return {
    onUnitPointerDown,
    onTilePointerDown,
    onMarkerPointerDown,
    onBoardPointerMove,
    onBoardPointerOut,
  };
}
